import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { PageHero } from '../components/shared/PageHero';

// WordPress Contact Form 7 配置
const WP_BASE_URL = 'https://zwheel-shop.com'.replace(/\/+$/, '');
const CF7_CONTACT_FORM_ID = '39572';
const CF7_CONTAINER_POST_ID = '0';
const CF7_UNIT_TAG = `wpcf7-f${CF7_CONTACT_FORM_ID}-p${CF7_CONTAINER_POST_ID}-o1`;
const CF7_ENDPOINT = CF7_CONTACT_FORM_ID
  ? `${WP_BASE_URL}/wp-json/contact-form-7/v1/contact-forms/${CF7_CONTACT_FORM_ID}/feedback`
  : '';

// 验证规则
const VALIDATION = {
  name: { min: 2, max: 50 },
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: { max: 25 },
  subject: { max: 100 },
  message: { min: 5, max: 2000 }
};

// Contact Form 7 响应状态对应的用户提示信息
const ERROR_MESSAGES: Record<string, string> = {
  validation_failed: 'Los datos del formulario no son válidos. Por favor, revisa los campos.',
  spam: 'No hemos podido enviar el mensaje. Por favor, inténtalo de nuevo.',
  mail_failed: 'Error del servidor. Por favor, inténtalo más tarde.',
  config_error: 'El formulario de contacto no está configurado correctamente.',
  default: 'Ha ocurrido un error. Por favor, inténtalo de nuevo.'
};

const SUBJECT_LABELS: Record<string, string> = {
  dealer: 'Convertirse en distribuidor',
  service: 'Consulta servicio postventa',
  other: 'Otro'
};

interface ContactForm7Response {
  status?: string;
  message?: string;
}

function getSubjectLabel(subject: string) {
  return SUBJECT_LABELS[subject] || subject;
}

function buildContactFormPayload(data: ContactPageFormData) {
  const payload = new window.FormData();

  payload.append('_wpcf7', CF7_CONTACT_FORM_ID);
  payload.append('_wpcf7_version', '6.0');
  payload.append('_wpcf7_locale', 'es_ES');
  payload.append('_wpcf7_unit_tag', CF7_UNIT_TAG);
  payload.append('_wpcf7_container_post', CF7_CONTAINER_POST_ID);
  payload.append('_wpcf7_posted_data_hash', '');
  payload.append('your-name', data.name);
  payload.append('your-email', data.email);
  payload.append('your-phone', data.phone);
  payload.append('your-subject', getSubjectLabel(data.subject));
  payload.append('your-message', data.message);
  payload.append('privacy-consent', data.privacyConsent ? '1' : '');

  return payload;
}

interface ContactPageFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  privacyConsent: boolean;
}

interface ValidationError {
  field: string;
  message: string;
}

// 前端表单验证
function validateForm(data: ContactPageFormData): ValidationError | null {
  // 验证姓名
  if (data.name.length < VALIDATION.name.min || data.name.length > VALIDATION.name.max) {
    return { 
      field: 'name', 
      message: `El nombre debe tener entre ${VALIDATION.name.min} y ${VALIDATION.name.max} caracteres.` 
    };
  }
  
  // 验证邮箱格式
  if (!VALIDATION.email.test(data.email)) {
    return { field: 'email', message: 'Por favor, introduce un email válido.' };
  }
  
  // 验证电话（可选字段，但如果填写需要符合长度限制）
  if (data.phone && data.phone.length > VALIDATION.phone.max) {
    return { 
      field: 'phone', 
      message: `El teléfono no puede superar ${VALIDATION.phone.max} caracteres.` 
    };
  }
  
  // 验证主题（可选字段）
  if (data.subject && data.subject.length > VALIDATION.subject.max) {
    return { 
      field: 'subject', 
      message: `El asunto no puede superar ${VALIDATION.subject.max} caracteres.` 
    };
  }
  
  // 验证消息内容
  if (data.message.length < VALIDATION.message.min || data.message.length > VALIDATION.message.max) {
    return { 
      field: 'message', 
      message: `El mensaje debe tener entre ${VALIDATION.message.min} y ${VALIDATION.message.max} caracteres.` 
    };
  }

  if (!data.privacyConsent) {
    return {
      field: 'privacyConsent',
      message: 'Debes aceptar la política de privacidad para enviar el formulario.'
    };
  }
  
  return null;
}

function Contact() {
  const [formData, setFormData] = useState<ContactPageFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacyConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const nextValue = e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
      ? e.target.checked
      : value;

    setFormData(prev => ({ ...prev, [name]: nextValue }));
    // 用户输入时清除错误提示
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // 前端验证
    const validationError = validateForm(formData);
    if (validationError) {
      setError(validationError.message);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!CF7_ENDPOINT) {
        setError(ERROR_MESSAGES.config_error);
        return;
      }

      const response = await fetch(CF7_ENDPOINT, {
        method: 'POST',
        body: buildContactFormPayload(formData)
      });
      
      const result: ContactForm7Response = await response.json();
      
      // 处理响应
      if (response.ok && result.status === 'mail_sent') {
        // 成功
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', privacyConsent: false });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        // 业务错误
        const errorMessage = (result.status && ERROR_MESSAGES[result.status]) || result.message || ERROR_MESSAGES.default;
        setError(errorMessage);
      }
    } catch (err) {
      // 网络错误或解析错误
      console.error('联系表单提交失败:', err);
      setError('Error de conexión. Por favor, verifica tu conexión a internet.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <PageHero
        title="Contáctanos"
        subtitle="Estamos aquí para ayudarte en cada paso del camino"
      />

      {/* 主体内容 - 表单与地图 */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* 左侧 - 联系表单 */}
            <AnimatedSection>
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 h-full flex flex-col">
                <div className="mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Envíanos un mensaje
                  </h2>
                  <p className="text-gray-600">
                    Completa el formulario y te contactaremos en 24 horas.
                  </p>
                </div>

                {/* 成功提示 */}
                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-800">¡Mensaje enviado con éxito! Te contactaremos pronto.</span>
                  </motion.div>
                )}

                {/* 错误提示 */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-red-800">{error}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
                        placeholder="+34 600 000 000"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Asunto *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow bg-white"
                      >
                        <option value="">Selecciona...</option>
                        <option value="dealer">Convertirse en distribuidor</option>
                        <option value="service">Consulta servicio postventa</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mensaje *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow resize-none flex-1 min-h-[120px]"
                      placeholder="¿En qué podemos ayudarte?"
                    />
                  </div>

                  <label className="flex items-start gap-3 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      name="privacyConsent"
                      checked={formData.privacyConsent}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                    />
                    <span>
                      He leído y acepto la{' '}
                      <a href="/legal/privacy" className="font-medium text-gray-900 underline underline-offset-2 hover:text-black">
                        política de privacidad
                      </a>.
                    </span>
                  </label>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full mt-auto"
                  >
                    {isSubmitting ? 'Enviando...' : (
                      <>
                        Enviar mensaje
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </AnimatedSection>

            {/* 右侧 - 地图与地址整合 */}
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full flex flex-col">
                {/* 地图 - 使用flex-1让其自适应填充 */}
                <div className="flex-1 min-h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2994.5!2d2.0989607755431994!3d41.32669455203318!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a49c6da9a26c2b%3A0xb81e3226c1222047!2sCarrer%20de%20la%20Riera%20Roja%2C%2012%2C%2008830%20Sant%20Boi%20de%20Llobregat%2C%20Barcelona!5e0!3m2!1ses!2ses!4v1764052901498!5m2!1ses!2ses"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '300px' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="ZWHEEL Barcelona"
                  ></iframe>
                </div>

                {/* 地址信息 */}
                <div className="p-6 md:p-8 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 地址 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Sede Central</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Carrer de la Riera Roja, 12<br />
                          08830 Sant Boi de Llobregat<br />
                          Barcelona, España
                        </p>
                      </div>
                    </div>

                    {/* 联系方式 */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Teléfono</p>
                          <a href="tel:+34936675437" className="text-gray-900 font-medium hover:text-black transition-colors">
                            +34 936 675 437
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                          <a href="mailto:info@zwheel.com" className="text-gray-900 font-medium hover:text-black transition-colors">
                            info@zwheel.com
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        <span className="font-medium text-gray-700">Horario:</span> Lun - Vie: 9:00 - 18:00
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
